import * as React from 'react';
import 'dayjs/locale/de';
import { styled } from '@mui/system';
import { useEffect } from "react";
import dayjs from "dayjs";
import { ChartContainer, ChartsReferenceLine, ChartsXAxis, ChartsYAxis, ResponsiveChartContainer } from "@mui/x-charts";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Unstable_NumberInput as BaseNumberInput } from "@mui/base/Unstable_NumberInput";
import { LinePlot } from "@mui/x-charts/LineChart";
import Button from "@mui/material/Button";
import {

  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

} from '@mui/material';

import '../styles.css'
import '../css/Configuration.css';
import { useGrowSetup } from '../GrowSetupContext';
import { useConfig } from './ConfigContext';

// --- VPD / RH helpers (kPa) ---
function saturationVaporPressureKPa(tC) {
  return 0.6108 * Math.exp((17.27 * tC) / (tC + 237.3));
}

function rhFromVpd(tC, vpdKPa) {
  const svp = saturationVaporPressureKPa(tC);
  if (!Number.isFinite(svp) || svp <= 0) return 0;
  const rh = 100 * (1 - (vpdKPa / svp));
  return Math.max(0, Math.min(100, rh));
}

export default function SmallConfigAbluft(props) {
  const theme = useTheme();
  const { state, dispatch, saveConfig } = useConfig();
  const { grow_setup, setGrowSetup } = useGrowSetup();

  const [isResponsive, setIsResponsive] = React.useState(false);
  const Container = isResponsive ? ResponsiveChartContainer : ChartContainer;
  const sizingProps = isResponsive ? {} : { width: 340, height: 300 };

  // ✅ Phase wird persistent aus config gelesen
  const phase = state.config.vpd_phase ?? 'veg';
  const targetVpd = phase === 'flower' ? 1.4 : 1.0;

  // ✅ Default setzen, falls Feld noch nicht existiert (alte Configs)
  useEffect(() => {
    if (!state.config.vpd_phase) {
      dispatch({ type: 'SET_FIELD', field: 'vpd_phase', value: 'veg' });
    }
  }, [state.config.vpd_phase, dispatch]);

  // ✅ Auto-calc RH when phase or temps change
  useEffect(() => {
    const tDay = parseFloat(state.config.temperatur_tag);
    const tNight = parseFloat(state.config.temperatur_nacht);


    if (!Number.isFinite(tDay) || !Number.isFinite(tNight)) return;

    const rhDay = Math.round(rhFromVpd(tDay, targetVpd));
    const rhNight = Math.round(rhFromVpd(tNight, targetVpd));

    const currentDay = parseInt(state.config.luftfeuchte_tag);
    const currentNight = parseInt(state.config.luftfeuchte_nacht);

    if (Number.isFinite(rhDay) && rhDay !== currentDay) {
      dispatch({ type: 'SET_FIELD', field: 'luftfeuchte_tag', value: rhDay });
    }
    if (Number.isFinite(rhNight) && rhNight !== currentNight) {
      dispatch({ type: 'SET_FIELD', field: 'luftfeuchte_nacht', value: rhNight });
    }
  }, [
    phase,
    targetVpd,
    state.config.temperatur_tag,
    state.config.temperatur_nacht,
    state.config.luftfeuchte_tag,
    state.config.luftfeuchte_nacht,
    dispatch
  ]);

  return (
    <Card sx={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, borderRadius: 3, minHeight: 632, p: 2, boxShadow: 3, maxWidth: 600 }}>
      <CardContent sx={{ minHeight: 632 }}>
        <Typography variant="h6" gutterBottom>
          Abluft
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Luftfeuchtigkeit
          </Typography>

          <Grid container spacing={2}>
            {/* ✅ Phase dropdown (persistent) */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="vpd-phase-label">Phase</InputLabel>
                <Select
                  labelId="vpd-phase-label"
                  label="Phase"
                  value={phase}
                  onChange={(e) =>
                    dispatch({ type: 'SET_FIELD', field: 'vpd_phase', value: e.target.value })
                  }
                >
                  <MenuItem value="veg">Wuchsphase (Ziel-VPD 1.0 kPa)</MenuItem>
                  <MenuItem value="flower">Blütephase (Ziel-VPD 1.4 kPa)</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Berechnung aus Temperatur Tag/Nacht ({state.config.temperatur_tag}°C / {state.config.temperatur_nacht}°C) → Ziel-VPD {targetVpd} kPa
              </Typography>
            </Grid>

            {/* RH day (read-only) */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Luftfeuchte Tag (automatisch)</Typography>
              <BaseNumberInput
                disabled
                slots={{
                  root: StyledInputRoot,
                  input: StyledInput,
                  incrementButton: StyledButton,
                  decrementButton: StyledButton,
                }}
                slotProps={{
                  input: { readOnly: true },
                  incrementButton: {
                    children: <AddIcon fontSize="small" />,
                    className: 'increment',
                    disabled: true,
                  },
                  decrementButton: {
                    children: <RemoveIcon fontSize="small" />,
                    disabled: true,
                  },
                }}
                {...props}
                value={parseInt(state.config.luftfeuchte_tag)}
              />
            </Grid>

            {/* RH night (read-only) */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Luftfeuchte Nacht (automatisch)</Typography>
              <BaseNumberInput
                disabled
                slots={{
                  root: StyledInputRoot,
                  input: StyledInput,
                  incrementButton: StyledButton,
                  decrementButton: StyledButton,
                }}
                slotProps={{
                  input: { readOnly: true },
                  incrementButton: {
                    children: <AddIcon fontSize="small" />,
                    className: 'increment',
                    disabled: true,
                  },
                  decrementButton: {
                    children: <RemoveIcon fontSize="small" />,
                    disabled: true,
                  },
                }}
                {...props}
                value={parseInt(state.config.luftfeuchte_nacht)}
              />
            </Grid>

            {/* deviation stays editable */}
            <Grid item xs={12}>
              <Typography variant="body2">Erlaubte Abweichung</Typography>
              <BaseNumberInput
                slots={{
                  root: StyledInputRoot,
                  input: StyledInput,
                  incrementButton: StyledButton,
                  decrementButton: StyledButton,
                }}
                slotProps={{
                  incrementButton: {
                    children: <AddIcon fontSize="small" />,
                    className: 'increment',
                  },
                  decrementButton: {
                    children: <RemoveIcon fontSize="small" />,
                  },
                }}
                {...props}
                value={parseFloat(state.config.abweichung_luftfeuchte)}
                min={0}
                step={0.5}
                max={10}
                onChange={(e, v, t) => {
                  dispatch({ type: 'SET_FIELD', field: 'abweichung_luftfeuchte', value: v });
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Container
            series={[{ type: 'line', data: [] }]}
            xAxis={[{
              data: [dayjs.startOfYesterday, dayjs.endOfYesterday],
              scaleType: 'band',
              id: 'x-axis-id',
            }]}
            yAxis={[
              { data: [30, 0], max: 35, min: 0, scaleType: 'linear', id: 'y-axis-id' },
              { data: [100, 50, 0], max: 100, min: 0, scaleType: 'linear', id: 'y-axis-right-id' },
            ]}
            {...sizingProps}
          >
            <LinePlot />
            <ChartsXAxis label="" position="bottom" axisId="x-axis-id" />
            <ChartsYAxis label="Luftfeuchte" position="left" axisId="y-axis-right-id" />

            <ChartsReferenceLine
              labelAlign="end"
              y={state.config.luftfeuchte_tag}
              label="Luftfeuchte Tag"
              axisId="y-axis-right-id"
              lineStyle={{ stroke: 'blue' }}
            />
            <ChartsReferenceLine
              labelAlign="end"
              y={state.config.luftfeuchte_nacht}
              label="Luftfeuchte Nacht"
              axisId="y-axis-right-id"
              lineStyle={{ stroke: 'blue' }}
            />
          </Container>
        </CardContent>

        {props.is_managed ? <div /> : (
          <Button fullWidth variant="contained" color="success" sx={{ mt: 2 }} onClick={saveConfig}>
            Speichern
          </Button>
        )}
      </CardContent>
    </Card>
  );


}
// --- styles unchanged ---
const blue = {
  100: '#daecff',
  200: '#b6daff',
  300: '#66b2ff',
  400: '#3399ff',
  500: '#007fff',
  600: '#0072e5',
  700: '#0059B2',
  800: '#004c99',
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const StyledInputRoot = styled('div')(
  ({ theme }) => `
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 400;
    color: ${theme.palette.mode === 'dark' ? grey[300] : grey[500]};
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
  `,
);

const StyledInput = styled('input')(
  ({ theme }) => `
    font-size: 0.875rem;
    font-family: inherit;
    font-weight: 400;
    line-height: 1.375;
    color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
    background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
    border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
    box-shadow: 0px 2px 4px ${
      theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'
    };
    border-radius: 8px;
    margin: 0 8px;
    padding: 10px 12px;
    outline: 0;
    min-width: 0;
    width: 4rem;
    text-align: center;

    &:hover {
      border-color: ${blue[400]};
    }

    &:focus {
      border-color: ${blue[400]};
      box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? blue[700] : blue[200]};
    }

    &:focus-visible {
      outline: 0;
    }

    &:disabled {
      opacity: 0.9;
      cursor: not-allowed;
    }
  `,
);

const StyledButton = styled('button')(
  ({ theme }) => `
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    box-sizing: border-box;
    line-height: 1.5;
    border: 1px solid;
    border-radius: 999px;
    border-color: ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
    background: ${theme.palette.mode === 'dark' ? grey[900] : grey[50]};
    color: ${theme.palette.mode === 'dark' ? grey[200] : grey[900]};
    width: 32px;
    height: 32px;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 120ms;

    &:hover {
      cursor: pointer;
      background: ${theme.palette.mode === 'dark' ? blue[700] : blue[500]};
      border-color: ${theme.palette.mode === 'dark' ? blue[500] : blue[400]};
      color: ${grey[50]};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 0;
    }

    &.increment {
      order: 1;
    }
  `,
);
